import { useMemo } from 'react';
import { r, s } from '@/theme/tokens';
import { chartBarSizing } from '../theme/chartBarSizing';
import { Box } from './Box';
import { useTheme } from './hooks';
import { Skeleton } from './Skeleton';

type StackedBarChartSkeletonProps = {
  height?: number;
  bars?: number;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const StackedBarChartSkeleton = ({
  height = 200,
  bars = 7,
}: StackedBarChartSkeletonProps): JSX.Element => {
  const categoryGapRatio = chartBarSizing.default.categoryGapRatio;
  const theme = useTheme();
  const yAxisGutter = `calc(${s('xl')} + ${s('sm')})`;
  const chartTop = s('md');
  const chartRight = s('lg');
  const chartBottom = `calc(${s('lg')} + ${s('xxs')})`;
  const chartInnerPaddingX = s('sm');
  const axisLeft = `calc(${yAxisGutter} + ${chartInnerPaddingX})`;
  const axisBottom = chartBottom;
  const barHeights = useMemo(() => {
    const resolvedBars = clamp(bars, 4, 18);
    return Array.from({ length: resolvedBars }, (_, index) => {
      const offset = Math.abs((index % 5) - 2);
      const multiplier = 0.4 + offset * 0.12;
      return clamp(height * multiplier, height * 0.35, height * 0.9);
    });
  }, [bars, height]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height }}>
      <Box
        sx={{
          position: 'absolute',
          left: axisLeft,
          right: chartRight,
          bottom: axisBottom,
          height: '1px',
          backgroundColor: theme.app.colors.divider,
          opacity: 0.7,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: axisLeft,
          top: chartTop,
          bottom: axisBottom,
          width: '1px',
          backgroundColor: theme.app.colors.divider,
          opacity: 0.7,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: axisLeft,
          right: chartRight,
          bottom: axisBottom,
          top: chartTop,
          display: 'flex',
          alignItems: 'flex-end',
          gap: `calc(${s('3xl')} * ${categoryGapRatio / chartBarSizing.default.categoryGapRatio})`,
          paddingLeft: chartInnerPaddingX,
          paddingRight: chartInnerPaddingX,
        }}
      >
        {barHeights.map((barHeight, index) => (
          <Box
            key={`bar-${index}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: s('xxxs'),
              flex: 1,
              minWidth: 0,
            }}
          >
            <Skeleton
              variant="rectangular"
              height={barHeight}
              sx={{
                width: '62%',
                maxWidth: '116px',
                mx: 'auto',
                borderRadius: `${r('md')} ${r('md')} ${r('none')} ${r('none')}`,
                bgcolor: theme.app.colors.border,
                opacity: 0.6,
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
